/**
 * 配信モード: viewer comments and gifts, drawn over the game, by the game.
 *
 * ## What this is, and what it is not
 *
 * TRAIDENT already has an OVERLAY automation target: a platform-owned browser
 * source the creator adds as a layer in OBS, served from
 * `apps/game-runtime/src/overlay.ts` with `frame-ancestors *` so a broadcaster
 * can frame it. That one is the platform's own surface. What it renders is what
 * the platform said to render, and a creator can point at it as evidence.
 *
 * This is the other thing. It runs *inside the game*, in the game's own
 * document, and it is the game that draws it. That has one honest consequence
 * worth stating at the top of the file rather than in an FAQ: a game can already
 * draw anything it likes on its own canvas, so nothing here can make an in-game
 * overlay trustworthy evidence of what a viewer said. It is a convenience — the
 * one every game would otherwise build slightly differently and slightly wrong —
 * not an attestation. ADR-0030 draws the line.
 *
 * What the platform *does* guarantee is upstream of this file: the text arriving
 * in `tp_live_chat` has been through `packages/rule-engine/src/viewer-text.ts`
 * on the server, before the Action was sent. This module's own defences are
 * about rendering, and they hold even if a game feeds it hand-written data.
 *
 * ## The constraints that shaped it
 *
 * - **No network, no assets.** A hosted game's CSP is `connect-src 'none'` and
 *   `font-src 'self' data:`. There is no fetch here, no web font, no image, no
 *   external stylesheet. Every glyph is text in the system font stack and the
 *   only "icon" is a character.
 * - **It must not become the game's problem.** The host element is a closed
 *   shadow root, so the game's CSS cannot reach in and this module's cannot leak
 *   out. `pointer-events: none` everywhere means it can never eat a click. There
 *   is no focusable node in it, so it cannot take focus from the game or move
 *   the tab order.
 * - **Off is the default and off is free.** Nothing is created, and no handler
 *   is registered, until `enable()` is called; `destroy()` returns the document
 *   to exactly the state it was in.
 * - **Quiet by default.** Bottom corner, four rows, translucent, gone after six
 *   seconds. A default that covers the play area is a default that gets turned
 *   off, and an overlay that is off shows nothing at all.
 */
import { guardText } from './text-guard.js';
const POSITIONS = [
    'BOTTOM_LEFT',
    'BOTTOM_RIGHT',
    'TOP_LEFT',
    'TOP_RIGHT',
];
const DEFAULTS = {
    position: 'BOTTOM_LEFT',
    opacity: 0.9,
    // Three, not four. Four rows of the default size cover the bottom third of a
    // 4/5 phone frame, which is where a game's own footer controls live.
    maxItems: 3,
    fadeAfterMs: 6000,
    // The folklore maximum. Being 647 below it means losing to every game that
    // uses the number everyone uses.
    zIndex: 2_147_483_647,
    announce: false,
    scale: 1,
};
/**
 * Coerce caller options into values the renderer can rely on.
 *
 * Every field here was previously trusted, and each one had a way to break the
 * layout that no type could catch — these arrive from JSON config, from a game's
 * own settings screen, from a `?debug=` parameter:
 *
 * - an unrecognised `position` matched none of the four corner rules, leaving a
 *   `position: fixed` element with all-auto offsets, which renders at the
 *   top-left of the document — on top of the score HUD;
 * - `zIndex: NaN` produced an invalid declaration and the computed value `auto`,
 *   dropping the layer below every positioned element in the game, and a
 *   negative value put it behind the background;
 * - `maxItems: NaN` made `Math.min(NaN, HARD_MAX_ROWS)` be `NaN` and
 *   `rows.length > NaN` always false, so the hard row bound never ran at all —
 *   400 messages produced 400 live nodes.
 */
function normalise(base, options) {
    const next = { ...base };
    if (options.position !== undefined && POSITIONS.includes(options.position)) {
        next.position = options.position;
    }
    next.opacity = finite(options.opacity, next.opacity, 0.15, 1);
    next.maxItems = Math.round(finite(options.maxItems, next.maxItems, 1, HARD_MAX_ROWS));
    next.fadeAfterMs = Math.round(finite(options.fadeAfterMs, next.fadeAfterMs, 0, 600_000));
    next.zIndex = Math.round(finite(options.zIndex, next.zIndex, 0, 2_147_483_647));
    next.scale = finite(options.scale, next.scale, 0.6, 2);
    if (typeof options.announce === 'boolean')
        next.announce = options.announce;
    if (options.container !== undefined)
        next.container = options.container;
    if (options.labels !== undefined)
        next.labels = { ...next.labels, ...options.labels };
    return next;
}
function finite(value, fallback, low, high) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return fallback;
    return Math.min(high, Math.max(low, value));
}
/** Longest label and body this module will draw, whatever it was handed. */
const MAX_LABEL = 24;
const MAX_TEXT = 140;
const MAX_GIFT = 32;
/** The words each kind falls back to, when the game names none. */
const DEFAULT_WORDS = {
    CHAT: '',
    GIFT: 'ギフト',
    FOLLOW: 'フォローしました',
    LIKE: 'いいね',
    SHARE: 'シェアしました',
    SUBSCRIBE: 'サブスクライブしました',
    MEMBERSHIP: 'メンバーになりました',
    MEMBERSHIP_GIFT: 'メンバーシップ',
    MEMBER_JOIN: 'メンバーシップを受け取りました',
    JOIN: '入室しました',
    FUNDING: 'サポート',
    STREAM_START: '配信がはじまりました',
    STREAM_END: '配信が終わりました',
};
/** Which override, if any, a kind reads from `labels`. */
const LABEL_KEYS = {
    GIFT: 'gift',
    FOLLOW: 'followed',
    LIKE: 'liked',
    SHARE: 'shared',
    SUBSCRIBE: 'subscribed',
    MEMBERSHIP: 'joinedMembership',
    MEMBERSHIP_GIFT: 'giftedMembership',
    MEMBER_JOIN: 'receivedMembership',
    JOIN: 'joined',
    FUNDING: 'supported',
    STREAM_START: 'streamStarted',
    STREAM_END: 'streamEnded',
};
/** Kinds whose row leads with a viewer name. The other two have no viewer. */
const CARRIES_ACTOR = new Set([
    'CHAT',
    'GIFT',
    'FOLLOW',
    'LIKE',
    'SHARE',
    'SUBSCRIBE',
    'MEMBERSHIP',
    'MEMBERSHIP_GIFT',
    'MEMBER_JOIN',
    'JOIN',
    'FUNDING',
]);
/** Kinds drawn as `<word> ×<count>` in the emphasised slot. */
const COUNTED = {
    GIFT: true,
    LIKE: true,
    MEMBERSHIP_GIFT: true,
};
/**
 * An amount, exactly as reported, with no conversion and no invented symbol.
 *
 * Integer arithmetic throughout: micros are integers and the decimal part is
 * assembled by padding rather than by dividing, so nothing here can produce
 * `0.30000000000000004` on a broadcast. The currency code leads because it is
 * the only honest presentation — this module cannot know whether `USD 5` should
 * be `$5`, and inventing a symbol is one short step from inventing a rate
 * (CLAUDE.md invariant 19).
 */
function formatMoney(amountMicros, currency) {
    if (!Number.isFinite(amountMicros) || amountMicros < 0)
        return currency;
    const micros = Math.trunc(amountMicros);
    const major = Math.trunc(micros / 1_000_000);
    const fraction = String(micros % 1_000_000)
        .padStart(6, '0')
        .replace(/0+$/, '');
    return fraction.length === 0
        ? `${currency} ${String(major)}`
        : `${currency} ${String(major)}.${fraction}`;
}
/**
 * A hard ceiling on live rows, independent of `maxItems`.
 *
 * `maxItems` is a look; this is a bound. They are separate numbers because a
 * game is free to set `maxItems` to something large and a flood should still
 * not be able to turn the overlay into an unbounded DOM.
 */
const HARD_MAX_ROWS = 12;
/**
 * Colour for a viewer's label, derived from their pseudonym.
 *
 * A stable colour per viewer is what makes a fast-moving chat readable at a
 * glance, and deriving it from `ref` means it needs no state and stays the same
 * across a reconnect. Hue only: saturation and lightness are fixed at the
 * measured values documented at the call site, so no viewer can land on a colour
 * that fails contrast against the pill.
 */
function hueFor(ref) {
    let hash = 0;
    for (let index = 0; index < ref.length; index += 1) {
        hash = (hash * 31 + ref.charCodeAt(index)) >>> 0;
    }
    return hash % 360;
}
/**
 * The stylesheet.
 *
 * Kept as one string and installed once per overlay. `adoptedStyleSheets` is
 * preferred where available because a constructed stylesheet is not subject to
 * the `style-src` directive at all; the `<style>` fallback is covered by the
 * `'unsafe-inline'` the game runtime already emits for styles.
 */
const CSS = `
:host { all: initial; }
.tp-layer {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* Bounded on both axes. A bottom-anchored column with no ceiling runs off the
     top of a short frame -- a landscape phone at 740x320 is 12 rows tall and the
     first ones are clipped mid-glyph while covering the game's HUD. */
  max-width: min(340px, 42vw);
  max-height: 40%;
  overflow: hidden;
  pointer-events: none;
  font-family: system-ui, -apple-system, "Hiragino Sans", "Noto Sans JP", sans-serif;
  /* px, not rem. Every rem in this sheet would be resolved against the *game's*
     root font-size, and \`html { font-size: 62.5% }\` is one of the most common
     idioms in CSS -- it would silently shrink the whole overlay to 62%, while the
     rem-based media query below still switched at the unscaled width. A shadow
     root does not isolate inherited or root-relative units. */
  font-size: 13px;
  line-height: 1.35;
  user-select: none;
  -webkit-user-select: none;
  /* Neutral direction. \`all: initial\` explicitly does not reset \`direction\` or
     \`unicode-bidi\` (CSS Cascade 3.2), so a game with <html dir="rtl"> would
     otherwise reverse the label and the body inside every row. */
  direction: ltr;
}
.tp-layer[data-corner="BOTTOM_LEFT"]  { left: 12px; bottom: 12px; align-items: flex-start; }
.tp-layer[data-corner="BOTTOM_RIGHT"] { right: 12px; bottom: 12px; align-items: flex-end; }
.tp-layer[data-corner="TOP_LEFT"]     { left: 12px; top: 12px; align-items: flex-start; }
.tp-layer[data-corner="TOP_RIGHT"]    { right: 12px; top: 12px; align-items: flex-end; }

.tp-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 10px;
  /* 0.9, not 0.62.
     The pill used to be 0.62 alpha inside a layer that was itself 0.82 opaque,
     compositing to 0.51 -- which over a white game produces a #838488 plate, and
     measured against that plate every one of the 360 label hues, the body text
     and the gift text all failed WCAG AA. The plate has to carry the contrast on
     its own, because the game behind it is not something this module can know. */
  background: rgba(10, 12, 18, 0.9);
  color: #f6f8fc;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.85);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 180ms ease-out, transform 180ms ease-out;
}
.tp-row[data-shown="1"] { opacity: 1; transform: translateY(0); }
.tp-row[data-leaving="1"] { opacity: 0; transform: translateY(-4px); }

.tp-label {
  flex: 0 0 auto;
  max-width: 9em;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Isolated so a right-to-left name cannot reorder the punctuation or the body
     beside it. \`text-guard\` removes explicit bidi *overrides*; strong RTL
     characters are legitimate text and reorder neutrals without any. */
  unicode-bidi: isolate;
}
.tp-body {
  flex: 1 1 auto;
  min-width: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
  unicode-bidi: isolate;
}
.tp-gift {
  flex: 0 0 auto;
  font-weight: 700;
  color: #ffd479;
  white-space: nowrap;
  unicode-bidi: isolate;
}
.tp-kind {
  flex: 0 0 auto;
  /* Was 0.75 opacity, which failed AA even on a mid-grey game. A dimmer colour
     that is still a real colour, rather than transparency over an unknown
     backdrop. */
  color: #c9cedb;
  font-size: 0.85em;
  white-space: nowrap;
}

@media (max-width: 480px) {
  .tp-layer { max-width: min(240px, 62vw); font-size: 12px; max-height: 45%; }
  .tp-label { max-width: 7em; }
}

@media (prefers-reduced-motion: reduce) {
  .tp-row { transition: opacity 1ms linear; transform: none; }
  .tp-row[data-shown="1"] { transform: none; }
  .tp-row[data-leaving="1"] { transform: none; }
}

/* Forced colours removes the plate and the shadow and overrides every colour, so
   the rows would otherwise sit as system-coloured text directly on the game's
   own content. A border and the system window colours put the pill back. */
@media (forced-colors: active) {
  .tp-row {
    background: Canvas;
    color: CanvasText;
    border: 1px solid CanvasText;
    text-shadow: none;
    forced-color-adjust: none;
  }
  .tp-label, .tp-gift, .tp-kind { color: CanvasText; }
}
`;
/**
 * Create the overlay layer.
 *
 * Exported separately from `TRYPORTALGame.enableStreamOverlay` so a game can
 * drive it from its own action handlers — or from nothing at all, in a scene
 * editor — without a live session.
 */
export function createStreamOverlay(options = {}) {
    const doc = typeof document === 'undefined' ? undefined : document;
    if (doc === undefined) {
        // A no-op rather than a throw: a game that runs its logic in a worker or
        // under SSR should not have to branch on the environment to call this.
        return {
            present: () => undefined,
            update: () => undefined,
            clear: () => undefined,
            destroy: () => undefined,
            destroyed: true,
        };
    }
    let settings = normalise({ ...DEFAULTS }, options);
    let destroyed = false;
    const host = doc.createElement('div');
    host.setAttribute('data-tryportal-stream-overlay', '');
    // The host itself is inert and takes no space; everything visible is the
    // fixed-position layer inside the shadow root.
    // `!important` on the inline styles.
    //
    // The host advertises a stable attribute selector, and a game whose stylesheet
    // said `[data-tryportal-stream-overlay] { pointer-events: auto !important }`
    // could otherwise turn this layer into a click target sitting over its own
    // controls. An author `!important` rule beats a plain inline declaration; an
    // inline `!important` declaration beats the author rule. This is the only
    // priority that makes the documented guarantee actually hold.
    host.style.setProperty('all', 'initial', 'important');
    host.style.setProperty('position', 'fixed', 'important');
    host.style.setProperty('inset', '0', 'important');
    host.style.setProperty('pointer-events', 'none', 'important');
    host.style.setProperty('z-index', String(settings.zIndex), 'important');
    // A shadow root, for CSS isolation in both directions: the game's stylesheet
    // cannot select anything in here, and nothing in here can match the game's
    // elements. That is the property that matters day to day — a game with a
    // global `div { box-sizing: content-box }` should not be able to break this
    // layout, and this layout should not be able to break the game's.
    //
    // Open rather than closed, deliberately. Closed sounds safer and buys nothing
    // here: the game owns this document, so a game that wants to tamper can delete
    // the host node, monkey-patch `attachShadow`, or simply not call this function
    // at all. Nothing rendered inside a game's own page can be evidence against
    // that game, which is why the platform's guarantee lives on the server and
    // ADR-0030 says so explicitly.
    //
    // What closed *does* cost is real: a game developer cannot inspect the overlay
    // in devtools, and the browser suite cannot assert on what it drew — which
    // would mean the one test that checks a hostile string renders inertly could
    // not be written.
    const root = host.attachShadow({ mode: 'open' });
    installStyles(root, doc);
    const layer = doc.createElement('div');
    layer.className = 'tp-layer';
    applyLayer(layer, settings);
    root.appendChild(layer);
    const rows = [];
    const home = settings.container ?? doc.body;
    home.appendChild(host);
    /**
     * Follow the fullscreen element.
     *
     * A fullscreen element is painted in the browser's *top layer*, which is above
     * every z-index there is. `permissions-policy` grants `fullscreen=(self)` and
     * the play shell passes `allow="fullscreen 'self'"`, so a game going fullscreen
     * is a supported path — and it made the whole overlay silently disappear, which
     * is the worst possible failure for a feature a creator switched on for a
     * broadcast. Re-parenting into the fullscreen element puts the layer back in
     * the same stacking context as the thing that took over the screen.
     */
    const followFullscreen = () => {
        if (destroyed)
            return;
        const full = doc.fullscreenElement;
        const target = full !== null && !full.contains(home) ? full : home;
        if (host.parentNode !== target)
            target.appendChild(host);
    };
    doc.addEventListener('fullscreenchange', followFullscreen);
    function removeRow(row) {
        if (row.fadeTimer !== undefined)
            clearTimeout(row.fadeTimer);
        if (row.removeTimer !== undefined)
            clearTimeout(row.removeTimer);
        row.node.remove();
        const index = rows.indexOf(row);
        if (index >= 0)
            rows.splice(index, 1);
    }
    function trim() {
        const limit = Math.min(settings.maxItems, HARD_MAX_ROWS);
        while (rows.length > limit) {
            const oldest = rows[0];
            if (oldest === undefined)
                break;
            removeRow(oldest);
        }
    }
    return {
        get destroyed() {
            return destroyed;
        },
        present(entry) {
            if (destroyed)
                return;
            // The game owns this document and may have removed the host — a scene
            // change, a framework re-render, a `body.innerHTML = ''`. Without this the
            // overlay keeps building rows and arming timers into a detached tree
            // forever, and the caller has no way to find out.
            if (!host.isConnected) {
                destroyed = true;
                for (const row of [...rows])
                    removeRow(row);
                return;
            }
            // Guarded here as well as on the server. This module is reachable from a
            // game's own code with arbitrary values, and the shape guarantees the
            // layout depends on — one line, bounded length, no direction overrides —
            // have to hold for those too.
            const label = guardText(entry.label, { maxLength: MAX_LABEL });
            const text = guardText(entry.text, { maxLength: MAX_TEXT });
            const gift = guardText(entry.giftLabel, { maxLength: MAX_GIFT });
            const row = doc.createElement('div');
            row.className = 'tp-row';
            /** The word for this kind: the game's, or this module's own. */
            const labelKey = LABEL_KEYS[entry.kind];
            const word = (labelKey === undefined ? undefined : settings.labels?.[labelKey]) ??
                DEFAULT_WORDS[entry.kind];
            if (CARRIES_ACTOR.has(entry.kind)) {
                const labelNode = doc.createElement('span');
                labelNode.className = 'tp-label';
                // `textContent`, never `innerHTML`. Every string that reaches this module
                // is viewer-influenced somewhere upstream, and there is no case in which
                // one of them should become markup.
                labelNode.textContent = label.length > 0 ? label : (settings.labels?.anonymous ?? '匿名');
                // 80% / 84%, not 78% / 74%.
                //
                // Saturation and lightness are fixed so the only thing that varies per
                // viewer is hue, and the pair is chosen by measurement rather than by eye:
                // over the pill composited on a *white* game — the worst case, and the one
                // a bright cartoon game actually presents — the previous values put all 360
                // hues below WCAG AA, the worst (blue, hue 240) at 1.53:1. These put every
                // hue at or above 5.13:1 while keeping enough saturation that two viewers
                // are still telling colours apart. Recomputing after any change to the pill
                // alpha is not optional; the two numbers are one decision.
                labelNode.style.setProperty('color', `hsl(${String(hueFor(entry.ref ?? ''))} 80% 84%)`);
                row.appendChild(labelNode);
            }
            if (entry.kind === 'CHAT') {
                // A chat entry whose body did not survive guarding is not drawn at all.
                // A row consisting of a name and nothing else tells the audience only
                // that something was removed.
                if (text.length === 0)
                    return;
                const body = doc.createElement('span');
                body.className = 'tp-body';
                body.textContent = text;
                row.appendChild(body);
            }
            else if (COUNTED[entry.kind] === true) {
                const countNode = doc.createElement('span');
                countNode.className = 'tp-gift';
                const count = Number.isFinite(entry.count) ? Math.max(1, Math.trunc(entry.count ?? 1)) : 1;
                // A gift names itself; a like and a gifted membership are named by the
                // kind, because there is no per-item label for either.
                const noun = entry.kind === 'GIFT' ? (gift.length > 0 ? gift : word) : word;
                countNode.textContent = `${noun} ×${String(Math.min(count, 9999))}`;
                row.appendChild(countNode);
            }
            else {
                const kindNode = doc.createElement('span');
                kindNode.className = 'tp-kind';
                // Money leads the row when the provider reported some, because that is
                // the fact the audience is looking for. The kind's own word is the
                // fallback for a payment that carried no amount.
                if (entry.kind === 'FUNDING' &&
                    typeof entry.amountMicros === 'number' &&
                    typeof entry.currency === 'string' &&
                    entry.currency.length > 0) {
                    const moneyNode = doc.createElement('span');
                    moneyNode.className = 'tp-gift';
                    moneyNode.textContent = formatMoney(entry.amountMicros, guardText(entry.currency, { maxLength: 8 }));
                    row.appendChild(moneyNode);
                }
                else {
                    kindNode.textContent = word;
                    row.appendChild(kindNode);
                }
            }
            // A membership tier and a milestone note are extra detail on a row that
            // already reads correctly without them, so they are appended rather than
            // being a separate branch. `level` is the creator's own word; `text` is
            // the viewer's and is guarded like a chat body.
            if (entry.kind !== 'CHAT') {
                const level = guardText(entry.level, { maxLength: MAX_GIFT });
                if (level.length > 0) {
                    const levelNode = doc.createElement('span');
                    levelNode.className = 'tp-kind';
                    levelNode.textContent = level;
                    row.appendChild(levelNode);
                }
                if (text.length > 0) {
                    const body = doc.createElement('span');
                    body.className = 'tp-body';
                    body.textContent = text;
                    row.appendChild(body);
                }
            }
            const topAnchored = settings.position.startsWith('TOP_');
            if (topAnchored)
                layer.insertBefore(row, layer.firstChild);
            else
                layer.appendChild(row);
            const live = { node: row, fadeTimer: undefined, removeTimer: undefined };
            rows.push(live);
            // Timers are armed *before* trimming. The other order leaks: `trim()` can
            // evict this very row (any time `maxItems` is small enough), `removeRow`
            // then clears the timers it can see, and the timer assigned afterwards
            // belongs to a row nobody holds a reference to any more. Measured at 50
            // outstanding timers after `destroy()` had returned.
            if (settings.fadeAfterMs > 0) {
                live.fadeTimer = setTimeout(() => {
                    row.setAttribute('data-leaving', '1');
                    live.removeTimer = setTimeout(() => {
                        removeRow(live);
                    }, 240);
                }, settings.fadeAfterMs);
            }
            trim();
            // Two frames rather than one: the first commits the node with its initial
            // (transparent) style, the second changes it, which is what gives the
            // transition something to animate from. Setting both in one frame makes
            // rows appear instantly.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    row.setAttribute('data-shown', '1');
                });
            });
        },
        update(next) {
            if (destroyed)
                return;
            settings = normalise(settings, next);
            host.style.setProperty('z-index', String(settings.zIndex));
            applyLayer(layer, settings);
            trim();
        },
        clear() {
            while (rows.length > 0) {
                const row = rows[0];
                if (row === undefined)
                    break;
                removeRow(row);
            }
        },
        destroy() {
            if (destroyed)
                return;
            destroyed = true;
            doc.removeEventListener('fullscreenchange', followFullscreen);
            for (const row of [...rows])
                removeRow(row);
            host.remove();
        },
    };
}
function applyLayer(layer, settings) {
    layer.setAttribute('data-corner', settings.position);
    layer.style.setProperty('opacity', String(settings.opacity));
    // `transform`, not `zoom`.
    //
    // `zoom` scales used values, so `max-width: min(340px, 42vw)` became 42vw *
    // scale — at scale 3 the layer was 126% of the viewport wide and the four rows
    // covered the entire screen. A transform scales the painted result and leaves
    // the box the layout computed alone, so the bound stays a bound.
    layer.style.setProperty('transform', `scale(${String(settings.scale)})`);
    layer.style.setProperty('transform-origin', settings.position.endsWith('_RIGHT') ? 'right' : 'left');
    if (settings.announce) {
        layer.setAttribute('role', 'log');
        layer.setAttribute('aria-live', 'polite');
        layer.removeAttribute('aria-hidden');
    }
    else {
        layer.removeAttribute('role');
        layer.removeAttribute('aria-live');
        layer.setAttribute('aria-hidden', 'true');
    }
}
function installStyles(root, doc) {
    const supportsConstructed = typeof CSSStyleSheet === 'function' &&
        'adoptedStyleSheets' in root &&
        // Safari shipped the class before `replaceSync`, so feature-detect the
        // method rather than the constructor.
        typeof CSSStyleSheet.prototype.replaceSync === 'function';
    if (supportsConstructed) {
        try {
            const sheet = new CSSStyleSheet();
            sheet.replaceSync(CSS);
            root.adoptedStyleSheets = [sheet];
            return;
        }
        catch {
            // Fall through to the element form. A CSP that forbids constructed
            // stylesheets is not a thing today, but a runtime that throws here would
            // otherwise take the whole overlay down.
        }
    }
    const style = doc.createElement('style');
    style.textContent = CSS;
    root.appendChild(style);
}
